import { environment } from "../../config/environment";
import React, { useState, useEffect } from "react";
import { customFetch } from "../../utils/Auth";
import { addDaysToDate, formatDateLong } from "./utils";
import PatientSummaryTable from "./PatientTable";
import PatientInfo from "./PatientInfo";
import ClientEventsTables from "./ClientEventsTables";
import { Toaster } from "../ui/Toaster";
import schedulePullConfig from "../../config/schedule-pull-config.json";

const DoctorInfo = ({ 
  selectedDoctor, 
  setSelectedDoctor, 
  doctorSearch, 
  setDoctorSearch, 
  allDoctors, 
  setAllDoctors, 
  filteredDoctors, 
  setFilteredDoctors,
  selectedPatient,
  setSelectedPatient,
  selectedPatientId,
  setSelectedPatientId,
  setIsLoadingPatientData,
  getPatientData,
  showTrace,
  setShowTrace,
  traceData,
  setTraceData,
  fetchTrace,
  fetchJobsByPatient
}) => {
  const [selectedDoctorFull, setSelectedDoctorFull] = useState(null);
  const [isLoadingDoctorFull, setIsLoadingDoctorFull] = useState(false);
  const [doctorDate, setDoctorDate] = useState("");
  const [doctorPatients, setDoctorPatients] = useState([]);
  const [doctorPatientsVersion, setDoctorPatientsVersion] = useState(0);
  const [isLoadingDoctorPatients, setIsLoadingDoctorPatients] = useState(false);
  const [showFullDoctorDetails, setShowFullDoctorDetails] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showUpdateMsg, setShowUpdateMsg] = useState(false);
  const [showFullPatientDetails, setShowFullPatientDetails] = useState(false);
  const [isEditPatientMode, setIsEditPatientMode] = useState(false);
  const [showPatientUpdateMsg, setShowPatientUpdateMsg] = useState(false);
  const [showDoctorEventLogs, setShowDoctorEventLogs] = useState(false);
  const [doctorClientEvents, setDoctorClientEvents] = useState([]);
  const [isLoadingDoctorClientEvents, setIsLoadingDoctorClientEvents] = useState(false);
  const [doctorClientEventsError, setDoctorClientEventsError] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduleDate, setScheduleDate] = useState(new Date().toISOString().split('T')[0]);
  const [scheduleData, setScheduleData] = useState(null);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);

  // Handler for Event Logs button
  const handleEventLogsClick = async () => {
    if (!selectedDoctor || !selectedDoctor.user_name) return;
    
    if (showDoctorEventLogs) {
      // Hide event logs section
      setShowDoctorEventLogs(false);
      setDoctorClientEvents([]);
      setDoctorClientEventsError(null);
    } else {
      // Show event logs section
      setShowDoctorEventLogs(true);
      
      // Fetch client events for this doctor
      setIsLoadingDoctorClientEvents(true);
      setDoctorClientEventsError(null);
      try {
        const params = new URLSearchParams();
        params.append('userName', selectedDoctor.user_name);
        
        const response = await customFetch(
          `${environment.apiUrl}/doctors/client-logs/search?${params.toString()}`,
          { method: "GET" }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch client events');
        }

        const data = await response.json();
        setDoctorClientEvents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching client events:', err);
        setDoctorClientEventsError('Failed to fetch client events: ' + err.message);
        setDoctorClientEvents([]);
      } finally {
        setIsLoadingDoctorClientEvents(false);
      }
    }
  };

  // Handler for Schedule button
  const handleScheduleClick = () => {
    setShowSchedule(!showSchedule);
  };

  // Handler for fetching schedule data
  const handleFetchSchedule = async () => {
    if (!selectedDoctorFull?.user_id) {
      setScheduleError("No doctor selected");
      return;
    }

    setIsLoadingSchedule(true);
    setScheduleError(null);
    setScheduleData(null);

    try {
      // Format date to remove leading zeros (e.g., 02/05/2025 -> 2/5/2025)
      const formattedDate = new Date(scheduleDate).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      });

      const response = await customFetch(
        `${environment.apiUrl}/doctors/update-schedule/${selectedDoctorFull.user_id}?date=${encodeURIComponent(formattedDate)}`,
        { method: "GET" }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch schedule data');
      }
      
      const data = await response.json();
      console.log('Received schedule data:', data);
      console.log('Lambda logs count:', data.lambdaLogs?.length || 0);
      setScheduleData(data);
    } catch (error) {
      console.error('Error fetching schedule data:', error);
      setScheduleError('Error fetching schedule data: ' + error.message);
    } finally {
      setIsLoadingSchedule(false);
    }
  };

  // Fetch all doctors when component mounts
  useEffect(() => {
    if (allDoctors.length === 0) {
      (async () => {
        try {
          const response = await customFetch(`${environment.apiUrl}/doctors/all-doctors`, { method: "GET" });
          const data = await response.json();
          setAllDoctors(Array.isArray(data) ? data : []);
        } catch (err) {
          setAllDoctors([]);
        }
      })();
    }
  }, [allDoctors.length, setAllDoctors]);

  // Fetch full doctor data when a doctor is selected
  useEffect(() => {
    if (selectedDoctor && selectedDoctor.user_id) {
      setIsLoadingDoctorFull(true);
      setSelectedDoctorFull(null);
      (async () => {
        try {
          const response = await customFetch(`${environment.apiUrl}/doctors/doctor-by-user-id/${selectedDoctor.user_id}`, { method: "GET" });
          const data = await response.json();
          setSelectedDoctorFull(data);
        } catch (err) {
          setSelectedDoctorFull({ error: "Failed to fetch doctor data" });
        } finally {
          setIsLoadingDoctorFull(false);
        }
      })();
    } else {
      setSelectedDoctorFull(null);
      setIsLoadingDoctorFull(false);
    }
  }, [selectedDoctor]);

  // Filter doctors as user types
  useEffect(() => {
    if (doctorSearch.trim()) {
      const search = doctorSearch.trim().toLowerCase();
      setFilteredDoctors(
        allDoctors.filter(doc => {
          const name = doc.user_name?.toLowerCase() || "";
          const id = doc.user_id?.toLowerCase() || "";
          const email = doc.user_email?.toLowerCase() || "";
          return name.includes(search) || id.includes(search) || email.includes(search);
        })
      );
    } else {
      setFilteredDoctors([]);
    }
  }, [doctorSearch, allDoctors, setFilteredDoctors]);

  // Set date to today when doctor is selected
  useEffect(() => {
    if (selectedDoctor) {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      setDoctorDate(`${yyyy}-${mm}-${dd}`);
    }
  }, [selectedDoctor]);

  // Fetch patients when doctor and date are selected
  useEffect(() => {
    if (selectedDoctor && doctorDate) {
      setIsLoadingDoctorPatients(true);
      setDoctorPatients([]);
      (async () => {
        try {
          const formattedDate = formatDateLong(doctorDate);
          const response = await customFetch(`${environment.apiUrl}/patients/summary/by-doctor-and-date?doctor_id=${encodeURIComponent(selectedDoctor.user_id)}&date_of_appt=${encodeURIComponent(formattedDate)}`, { method: "GET" });
          const data = await response.json();
          setDoctorPatients(Array.isArray(data) ? data : []);
        } catch (err) {
          setDoctorPatients([]);
        } finally {
          setIsLoadingDoctorPatients(false);
        }
      })();
    } else {
      setDoctorPatients([]);
    }
  }, [selectedDoctor, doctorDate, doctorPatientsVersion]);

  // Clear selected patient when doctorDate changes
  useEffect(() => {
    setSelectedPatient(null);
  }, [doctorDate, setSelectedPatient]);

  return (
    <div className="flex flex-col py-8 w-full">
      {/* Add a new doctor button when no doctor is selected */}
      {!selectedDoctor && (
        <button
          className="mb-3 px-4 py-2 bg-ktx-purple text-white rounded hover:bg-purple-700 transition-colors text-sm text-left w-auto"
          type="button"
          onClick={() => alert('Not implemented yet')}
          style={{ alignSelf: 'flex-start' }}
        >
          Add a new doctor
        </button>
      )}
      <label htmlFor="doctorSearch" className="block text-sm font-medium text-gray-700 mb-2 w-full text-left">
        Doctor Name
      </label>
      <input
        id="doctorSearch"
        type="text"
        autoComplete="off"
        value={doctorSearch}
        onChange={e => setDoctorSearch(e.target.value)}
        placeholder="Type to search for a doctor..."
        className="w-[350px] px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-ktx-purple mb-2 text-left"
      />
      {doctorSearch && filteredDoctors.length > 0 && (
        <div className="w-full max-h-56 overflow-y-auto border border-gray-200 rounded-md bg-white shadow z-10">
          {filteredDoctors.map(doc => (
            <div
              key={doc.user_id}
              className={`px-4 py-2 cursor-pointer hover:bg-purple-100 ${selectedDoctor && selectedDoctor.user_id === doc.user_id ? 'bg-purple-200' : ''}`}
              onClick={() => {
                setSelectedDoctor(doc);
                setDoctorSearch("");
                setSelectedPatient(null);
              }}
            >
              <div className="font-semibold text-gray-900">
                {doc.user_name} {doc.user_email ? <span className="text-xs text-gray-500">({doc.user_email})</span> : null}
              </div>
              <div className="text-xs text-gray-500">{doc.user_id}</div>
            </div>
          ))}
        </div>
      )}
      {doctorSearch && filteredDoctors.length === 0 && (
        <div className="w-full px-4 py-2 text-gray-400">No doctors found.</div>
      )}

      {selectedDoctorFull && !isLoadingDoctorFull && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-4 w-full overflow-auto">
          <h3 className="text-lg font-semibold text-ktx-purple mb-2">Doctor Details</h3>
          <div className="overflow-x-auto mb-2">
            <table className="min-w-[350px] w-full text-sm table-fixed">
              <tbody>
                {/* Summary fields */}
                <tr><td className="font-semibold pr-2 py-1 min-w-[140px]">Name</td><td className="break-words">{selectedDoctorFull.user_name || '-'}</td></tr>
                <tr><td className="font-semibold pr-2 py-1 min-w-[140px]">Email</td><td className="break-words">{selectedDoctorFull.user_email || '-'}</td></tr>
                <tr><td className="font-semibold pr-2 py-1 min-w-[140px]">Specialty</td><td className="break-words">{selectedDoctorFull.specialty || '-'}</td></tr>
                <tr><td className="font-semibold pr-2 py-1 min-w-[140px]">Organization</td><td className="break-words">{selectedDoctorFull.organization || '-'}</td></tr>
                <tr><td className="font-semibold pr-2 py-1 min-w-[140px]">Super Org</td><td className="break-words">{selectedDoctorFull.super_org || '-'}</td></tr>
                <tr>
                  <td className="font-semibold pr-2 py-1 min-w-[140px]">Onco EMR Link</td>
                  <td className="break-words">
                    {selectedDoctorFull.onco_emr_url ? (
                      <a
                        href={selectedDoctorFull.onco_emr_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-ktx-purple underline break-all"
                      >
                        {selectedDoctorFull.onco_emr_url}
                      </a>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
                {/* Full details fields, conditionally rendered */}
                {showFullDoctorDetails && ([
                  <tr key="phone"><td className="font-semibold pr-2 py-1 min-w-[140px]">Phone Number</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.phone_number || ''} /> : (selectedDoctorFull.phone_number || '-')}</td></tr>,
                  <tr key="npi"><td className="font-semibold pr-2 py-1 min-w-[140px]">NPI</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.npi || ''} /> : (selectedDoctorFull.npi || '-')}</td></tr>,
                  <tr key="qa"><td className="font-semibold pr-2 py-1 min-w-[140px]">QA</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.qa || ''} /> : (selectedDoctorFull.qa || '-')}</td></tr>,
                  <tr key="qac"><td className="font-semibold pr-2 py-1 min-w-[140px]">QA Checklist</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.qa_checklist || ''} /> : (selectedDoctorFull.qa_checklist || '-')}</td></tr>,
                  <tr key="model"><td className="font-semibold pr-2 py-1 min-w-[140px]">Model Type</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.model_type || ''} /> : (selectedDoctorFull.model_type || '-')}</td></tr>,
                  <tr key="turnaround"><td className="font-semibold pr-2 py-1 min-w-[140px]">Turnaround Time</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.turnaround_time || ''} /> : (selectedDoctorFull.turnaround_time || '-')}</td></tr>,
                  <tr key="subscribed"><td className="font-semibold pr-2 py-1 min-w-[140px]">Subscribed?</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.is_subscribed ? 'Yes' : 'No'} /> : (selectedDoctorFull.is_subscribed ? 'Yes' : 'No')}</td></tr>,
                  <tr key="subid"><td className="font-semibold pr-2 py-1 min-w-[140px]">Subscription ID</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.subscription_id || ''} /> : (selectedDoctorFull.subscription_id || '-')}</td></tr>,
                  <tr key="subend"><td className="font-semibold pr-2 py-1 min-w-[140px]">Subscription End</td><td className="break-words">{isEditMode ? <input className="w-[350px] px-3 py-0.5 border rounded text-left" defaultValue={selectedDoctorFull.subscription_end_date || ''} /> : (selectedDoctorFull.subscription_end_date || '-')}</td></tr>,
                  <tr key="created"><td className="font-semibold pr-2 py-1 min-w-[140px]">Created At</td><td className="break-words">{selectedDoctorFull.created_at ? new Date(selectedDoctorFull.created_at).toLocaleString() : '-'}</td></tr>,
                  <tr key="userid"><td className="font-semibold pr-2 py-1 min-w-[140px]">User ID</td><td className="break-words">{selectedDoctorFull.user_id || '-'}</td></tr>,
                  <tr key="pw"><td className="font-semibold pr-2 py-1 min-w-[140px]">Password</td><td className="break-words">{selectedDoctorFull.user_password ? '••••••••' : '-'}</td></tr>,
                  <tr key="ehrpw"><td className="font-semibold pr-2 py-1 min-w-[140px]">EHR Password</td><td className="break-words">{selectedDoctorFull.ehr_password ? '••••••••' : '-'}</td></tr>,
                  <tr key="token"><td className="font-semibold pr-2 py-1 min-w-[140px]">Access Token</td><td className="break-words">{selectedDoctorFull.access_token ? '••••••••' : '-'}</td></tr>
                ])}
              </tbody>
            </table>
          </div>
          {/* Doctor updated message */}
          {showUpdateMsg && (
            <div className="text-green-600 text-sm font-semibold mt-2 mb-1">Doctor Updated</div>
          )}
          <div className="flex justify-end mt-2 gap-2">
            {/* Show full details button when details are hidden */}
            {!showFullDoctorDetails && !isEditMode && (
              <button
                className="text-xs text-ktx-purple underline"
                onClick={() => setShowFullDoctorDetails(true)}
                type="button"
              >
                Show full details
              </button>
            )}
            {/* Hide/Edit buttons when details are visible and not in edit mode */}
            {showFullDoctorDetails && !isEditMode && (
              <>
                <button
                  className="text-xs text-ktx-purple underline"
                  onClick={() => setShowFullDoctorDetails(false)}
                  type="button"
                >
                  Hide full details
                </button>
                <button
                  className="text-xs text-blue-600 underline"
                  onClick={() => setIsEditMode(true)}
                  type="button"
                >
                  Edit
                </button>
              </>
            )}
            {/* Cancel/Save buttons in edit mode */}
            {showFullDoctorDetails && isEditMode && (
              <>
                <button
                  className="text-xs text-gray-600 underline"
                  onClick={() => setIsEditMode(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className="text-xs text-green-600 underline"
                  onClick={() => {
                    setIsEditMode(false);
                    setShowUpdateMsg(true);
                    setTimeout(() => setShowUpdateMsg(false), 2000);
                  }}
                  type="button"
                >
                  Save
                </button>
              </>
            )}
          </div>
        </div>
      )}
      
      {isLoadingDoctorFull && (
        <div className="mt-2 text-gray-500">Loading doctor data...</div>
      )}
      {/* Date picker for doctor selection */}
      {selectedDoctor && !isLoadingDoctorFull && (
        <div className="flex items-center gap-2 mt-4">
          <button
            type="button"
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
            onClick={() => {
              if (doctorDate) {
                setDoctorDate(addDaysToDate(doctorDate, -1));
              }
            }}
            aria-label="Previous day"
          >
            &#8592;
          </button>
          <input
            type="date"
            className="w-[350px] px-2 py-1 border border-gray-300 rounded"
            value={doctorDate}
            onChange={e => setDoctorDate(e.target.value)}
          />
          <button
            type="button"
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
            onClick={() => {
              if (doctorDate) {
                setDoctorDate(addDaysToDate(doctorDate, 1));
              }
            }}
            aria-label="Next day"
          >
            &#8594;
          </button>
        </div>
      )}

      {/* Show patients for doctor and date */}
      {isLoadingDoctorPatients && (
        <div className="mt-4 text-gray-500">Loading patients for this doctor and date...</div>
      )}
      {!isLoadingDoctorPatients && doctorPatients && doctorPatients.length > 0 && (
        <div className="mt-4 w-full">
          <PatientSummaryTable
            patients={doctorPatients}
            onPatientIdClick={async (id) => {
              setIsLoadingPatientData(true);
              setSelectedPatientId(id);
              setSelectedPatient(null);
              try {
                const patient = await getPatientData(id);
                setSelectedPatient(patient);
              } finally {
                setIsLoadingPatientData(false);
              }
            }}
            selectedPatientId={selectedPatientId}
            paginated={true}
            onDeletionChange={(patientId) => {
              if (String(selectedPatient?.patient_id) === String(patientId)) {
                setSelectedPatient(null);
              }
              setDoctorPatientsVersion((version) => version + 1);
            }}
          />
        </div>
      )}
      
      {/* Show selected patient details in doctor tab */}
      {selectedPatient && (
        <PatientInfo
          selectedPatient={selectedPatient}
          setSelectedPatient={setSelectedPatient}
          showFullPatientDetails={showFullPatientDetails}
          setShowFullPatientDetails={setShowFullPatientDetails}
          isEditPatientMode={isEditPatientMode}
          setIsEditPatientMode={setIsEditPatientMode}
          showPatientUpdateMsg={showPatientUpdateMsg}
          setShowPatientUpdateMsg={setShowPatientUpdateMsg}
          showTrace={showTrace}
          setShowTrace={setShowTrace}
          traceData={traceData}
          fetchTrace={fetchTrace}
          fetchJobsByPatient={fetchJobsByPatient}
        />
      )}

      {/* Action Buttons */}
      {selectedDoctor && (
        <div className="mt-6 flex gap-3">
          <button
            className={`px-4 py-2 text-white rounded transition-colors text-sm ${
              showDoctorEventLogs 
                ? 'bg-ktx-purple border-2 border-white shadow-lg' 
                : 'bg-gray-600 hover:bg-gray-700'
            }`}
            type="button"
            onClick={handleEventLogsClick}
          >
            {showDoctorEventLogs ? 'Hide Event Logs' : 'Event Logs'}
          </button>
          {/* Show Schedule button only for configured super_orgs */}
          {selectedDoctorFull && schedulePullConfig.super_org.includes(selectedDoctorFull.super_org) && (
            <button
              className={`px-4 py-2 text-white rounded transition-colors text-sm ${
                showSchedule 
                  ? 'bg-ktx-purple border-2 border-white shadow-lg' 
                  : 'bg-gray-600 hover:bg-gray-700'
              }`}
              type="button"
              onClick={handleScheduleClick}
            >
              {showSchedule ? 'Hide Schedule' : 'Schedule'}
            </button>
          )}
        </div>
      )}
      
      {/* Show doctor event logs */}
      {showDoctorEventLogs && (
        <div className="mt-8 w-full">
          <ClientEventsTables 
            logs={doctorClientEvents} 
            isLoading={isLoadingDoctorClientEvents} 
            error={doctorClientEventsError} 
          />
        </div>
      )}
      
      {/* Show schedule section */}
      {showSchedule && (
        <div className="mt-6 w-full">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Schedule</h3>
            
            <div className="flex items-center gap-4 mb-4">
              <label htmlFor="schedule-date" className="text-sm font-medium text-gray-700">
                Select Date:
              </label>
              <input
                id="schedule-date"
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ktx-purple focus:border-transparent"
              />
            </div>
            
            <div className="flex gap-3 mb-4">
              <button
                onClick={handleFetchSchedule}
                disabled={isLoadingSchedule}
                className="px-4 py-2 bg-ktx-purple text-white rounded hover:bg-purple-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                type="button"
              >
                {isLoadingSchedule ? 'Loading...' : 'Fetch Schedule Data'}
              </button>
            </div>

            {/* Loading state */}
            {isLoadingSchedule && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-gray-600">Loading schedule data...</p>
              </div>
            )}

            {/* Error state */}
            {scheduleError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-600">{scheduleError}</p>
              </div>
            )}

            {/* Success state - Lambda response */}
            {scheduleData && !isLoadingSchedule && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-md font-semibold text-gray-800 mb-2">Schedule Processing Result:</h4>
                {scheduleData.success ? (
                  <div>
                    <div className="mb-3">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Success
                      </span>
                    </div>
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Lambda Response:</h5>
                      <pre className="bg-white border border-gray-300 rounded p-3 overflow-auto text-sm text-gray-800 whitespace-pre-wrap">
                        {JSON.stringify(scheduleData.lambdaResponse, null, 2)}
                      </pre>
                    </div>
                    
                    {/* Lambda Logs */}
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Lambda Execution Logs:</h5>
                      {scheduleData.lambdaLogs && scheduleData.lambdaLogs.length > 0 ? (
                        <div className="bg-black text-green-400 border border-gray-300 rounded p-3 overflow-auto text-xs font-mono max-h-64">
                          {scheduleData.lambdaLogs.map((log, index) => (
                            <div key={index} className="mb-1">
                              <span className="text-gray-400">[{log.timestamp}]</span> {log.message}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="bg-gray-100 border border-gray-300 rounded p-3 text-sm text-gray-600">
                          No logs available for this execution. This could be because:
                          <ul className="list-disc list-inside mt-2 text-xs">
                            <li>Logs are still being written to CloudWatch</li>
                            <li>The Lambda function didn't produce any console output</li>
                            <li>There was an issue retrieving logs from CloudWatch</li>
                          </ul>
                        </div>
                      )}
                    </div>
                    <details className="text-sm">
                      <summary className="cursor-pointer text-gray-600 hover:text-gray-800">Show Original Data</summary>
                      <pre className="bg-gray-100 border border-gray-300 rounded p-3 mt-2 overflow-auto text-xs text-gray-600 whitespace-pre-wrap">
                        {JSON.stringify(scheduleData.originalData, null, 2)}
                      </pre>
                    </details>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded p-3">
                    <p className="text-red-600 font-medium">Error: {scheduleData.error}</p>
                    {scheduleData.details && (
                      <p className="text-red-500 text-sm mt-1">{scheduleData.details}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <Toaster />
    </div>
  );
};

export default DoctorInfo; 
